import React, { useEffect } from 'react';
import { DefaultButton, IContextualMenuItem } from '@fluentui/react';
import FilterPanel, { FilterOptionProps } from '../../../../../uiframeworks/filters/panel';
import SubsidiariesService from '../../../../../../services/data/subsidiaries';
import UserService from '../../../../../../services/users/user';
import CompanyService from '../../../../../../services/users/company';
import ISubsidiariesResourceShort from '../../../../../../props/data/subsidiaries';
import { IUserResourceShortProps } from '../../../../../../props/users/user';
import { ICompanyResourceShort } from '../../../../../../props/users/company';

interface IncomeInvoicesListFilterButtonProps {
    filters?: TFilterBy[];
    selected: FilterOptionProps[];
    onChange(selected: FilterOptionProps[]): void;
}

export type TFilterBy = 'subsidiary' | 'customer' | 'status';

const IncomeInvoicesListFilterButton: React.FC<IncomeInvoicesListFilterButtonProps> = (props: IncomeInvoicesListFilterButtonProps) => {
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();
    const [by, setBy] = React.useState<string | undefined>();
    const [selected, setSelected] = React.useState<FilterOptionProps[]>(props.selected);

    useEffect(() => {
        setSelected(props.selected);
    }, [props.selected]);

    const _onFilterApplied = (_selected: FilterOptionProps[]) => {
        props.onChange(_selected);
        setActiveSurface(undefined);
        setBy(undefined);
    };

    const _onCancelled = () => {
        setBy(undefined);
        setActiveSurface(undefined);
    };

    const _onRenderOptions = async (): Promise<FilterOptionProps[]> => {
        if (by) {
            const key = by.toLowerCase().split(" ").join("_");
            const options: FilterOptionProps[] = [];

            if (key === 'subsidiary') {
                const results = (await SubsidiariesService.retrieve("top=all"));
                results.forEach((res: ISubsidiariesResourceShort) => {
                    options.push({
                        filterby: key,
                        filterbyText: 'Subsidiary',
                        key: res.id,
                        title: res.name,
                        description: res.npwp,
                        metadata: res
                    });
                });
            } else if (key === 'customer') {
                const companies = (await CompanyService.retrieve("top=all")).data;
                companies.forEach((com: ICompanyResourceShort) => {
                    options.push({
                        filterby: key,
                        filterbyText: 'Customer / Company',
                        key: com.id,
                        title: com.name,
                        metadata: com
                    });
                });

                const users = (await UserService.retrieve("rolesGroup=customer&top=all")).data;
                users.forEach((res: IUserResourceShortProps) => {
                    if (!options.some(o => o.key === res.id)) {
                        options.push({
                            filterby: key,
                            filterbyText: 'Customer / Company',
                            key: res.id,
                            title: res.name,
                            metadata: res
                        });
                    }
                });
            } else if (key === 'status') {
                const statuses = [
                    { key: 'Unpaid', title: 'Unpaid' },
                    { key: 'Paid', title: 'Paid' },
                    { key: 'Cancelled', title: 'Cancelled' }
                ];
                statuses.forEach((s) => {
                    options.push({
                        filterby: key,
                        filterbyText: 'Status',
                        key: s.key,
                        title: s.title
                    });
                });
            }

            return options;
        } else {
            return [];
        }
    };

    const renderFilterButton = () => {
        const items: IContextualMenuItem[] = [];
        const filteredby: string[] | undefined = props.filters ? (props.filters || []).map((f) => f.toLowerCase()) : undefined;

        if (!filteredby || (filteredby || "").indexOf('customer') > -1) {
            items.push({
                key: 'customer',
                text: 'By customer / company',
                onClick: () => {
                    setBy('customer');
                    setActiveSurface('filter');
                }
            });
        }

        if (!filteredby || (filteredby || "").indexOf('subsidiary') > -1) {
            items.push({
                key: 'subsidiary',
                text: 'By subsidiary',
                onClick: () => {
                    setBy('subsidiary');
                    setActiveSurface('filter');
                }
            });
        }

        if (!filteredby || (filteredby || "").indexOf('status') > -1) {
            items.push({
                key: 'status',
                text: 'By status',
                onClick: () => {
                    setBy('status');
                    setActiveSurface('filter');
                }
            });
        }

        return <DefaultButton menuIconProps={{ iconName: "Filter" }} text={"Filter"} menuProps={{ items }} />;
    };

    return <>
        {renderFilterButton()}
        {activeSurface === 'filter' && by ? <>
            <FilterPanel title={"Filter by " + by}
                onRenderOptions={_onRenderOptions}
                selected={selected}
                onCancel={_onCancelled}
                onApply={_onFilterApplied} />
        </> : null}
    </>;
};

export default IncomeInvoicesListFilterButton;
